<template>
    <div>
        <section class="mc-card" aria-labelledby="customer-return-create-title">
            <div class="mc-card__head">
                <h2 id="customer-return-create-title" class="mc-card__title">Nuovo reso cliente</h2>
                <mc-status-chip tone="warn" label="Scrittura Sage" />
            </div>
            <div>
                <v-alert v-if="errors.length" type="error" text>
                    <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>
                <v-alert v-if="createResult" type="success" text dense>
                    <strong>{{ createResult.message }}</strong>
                </v-alert>
                <v-alert v-if="createResult && createResult.warnings && createResult.warnings.length" type="warning" text dense>
                    <div v-for="warning in createResult.warnings" :key="warning">{{ warning }}</div>
                </v-alert>
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            outlined dense hide-details="auto"
                            color="var(--brand)"
                            :menu-props="{ contentClass: 'mc-root mc-customer-return mc-return-menu' }"
                            v-model="draft.customerCode"
                            :items="customers"
                            item-value="codice"
                            :item-text="customerLabel"
                            :loading="loadingCustomers"
                            label="Cliente consegnato *"
                            clearable
                            @change="onCustomerChange"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            outlined dense hide-details="auto"
                            color="var(--brand)"
                            v-if="addresses.length === 1"
                            :value="addressLabel(addresses[0])"
                            label="Indirizzo cliente *"
                            readonly
                        />
                        <v-autocomplete
                            outlined dense hide-details="auto"
                            color="var(--brand)"
                            :menu-props="{ contentClass: 'mc-root mc-customer-return mc-return-menu' }"
                            v-else
                            v-model="draft.addressCode"
                            :items="addresses"
                            item-value="codice"
                            :item-text="addressLabel"
                            :loading="loadingAddresses"
                            :disabled="!draft.customerCode"
                            label="Indirizzo cliente *"
                            clearable
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field v-model.trim="draft.customerDocument" outlined dense hide-details="auto" color="var(--brand)" label="Nr. DDT cliente *" counter="20" maxlength="20" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-menu v-model="customerDocumentDateMenu" :close-on-content-click="false" offset-y min-width="290" content-class="mc-root mc-customer-return mc-return-menu">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    outlined dense hide-details="auto"
                                    color="var(--brand)"
                                    v-bind="attrs"
                                    :value="customerDocumentDateItalian"
                                    label="Data DDT cliente *"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker
                                color="var(--brand)"
                                v-model="draft.customerDocumentDate"
                                locale="it-IT"
                                :first-day-of-week="1"
                                @input="customerDocumentDateMenu = false"
                            />
                        </v-menu>
                    </v-col>
                </v-row>
                <customer-return-lines
                    :lines="draft.lines"
                    :customer-code="draft.customerCode"
                    :articles="deliveredArticles"
                    :loading-articles="loadingDeliveredArticles"
                    @add="addLine"
                    @remove="removeLine"
                />
            </div>
            <div class="mc-card__foot mc-return-actions">
                <v-btn text @click="resetDraft">Nuova bozza</v-btn>
                <div class="mc-return-actions__submit">
                    <v-btn outlined @click="saveDraft"><v-icon left>mdi-content-save-outline</v-icon>Salva bozza locale</v-btn>
                    <v-btn depressed class="mc-return-primary" :loading="creating" :disabled="Boolean(createResult)" @click="requestCreation">
                        <v-icon left>mdi-cloud-upload</v-icon>Crea reso in Sage
                    </v-btn>
                </div>
            </div>
        </section>
        <v-dialog v-model="confirming" max-width="520">
            <v-card class="mc-root mc-customer-return mc-dialog">
                <div class="mc-dialog__head"><h2 class="mc-dialog__title">Confermare la creazione?</h2></div>
                <div class="mc-dialog__body">
                    Verrà creato un reso cliente NCF in Sage per il DDT <strong>{{ draft.customerDocument }}</strong>.
                </div>
                <div class="mc-dialog__foot">
                    <v-btn text @click="confirming = false">Annulla</v-btn>
                    <v-btn depressed class="mc-return-primary" :loading="creating" @click="createReturn">Conferma e crea</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import CustomerReturnLines from "./CustomerReturnLines";
import McStatusChip from "@/assets/mecom/vue/McStatusChip.vue";
import { date } from "@/assets/mecom/js/format.mjs";
import {
    CUSTOMER_RETURN_DRAFT_KEY,
    buildCustomerReturnPayload,
    localTodayIso,
    newCustomerReturnDraft,
    newReturnLine,
    validateCustomerReturnDraft,
    validateDeliveredQuantities,
} from "./customerReturnDraft.mjs";

const TODAY_CACHE_SUFFIX = new Date().toISOString().slice(0, 10);
const CUSTOMERS_CACHE_KEY = `qualita_customer_return_customers_last_6_months_all_ddt_${TODAY_CACHE_SUFFIX}`;
const ADDRESSES_CACHE_PREFIX = "qualita_customer_return_addresses";
const DELIVERED_ARTICLES_CACHE_PREFIX = "qualita_customer_return_delivered_articles_with_quantity_last_6_months_all_ddt";

export default {
    name: "CustomerReturnCreate",
    components: { CustomerReturnLines, McStatusChip },
    props: {
        demoMode: { type: Boolean, default: false },
    },
    data: () => ({
        draft: newCustomerReturnDraft(),
        customers: [],
        addresses: [],
        deliveredArticles: [],
        loadingCustomers: false,
        loadingAddresses: false,
        loadingDeliveredArticles: false,
        errors: [],
        createResult: null,
        creating: false,
        confirming: false,
        customerDocumentDateMenu: false,
    }),
    computed: {
        customerDocumentDateItalian() {
            if (!this.draft.customerDocumentDate) return "";
            return date(this.draft.customerDocumentDate);
        },
    },
    async mounted() {
        const [savedDraft, cachedCustomers] = await Promise.all([
            this.$vlf.getItem(CUSTOMER_RETURN_DRAFT_KEY),
            this.$vlf.getItem(CUSTOMERS_CACHE_KEY),
        ]);
        if (savedDraft) {
            this.draft = savedDraft;
            if (!this.draft.customerDocumentDate) this.draft.customerDocumentDate = localTodayIso();
        }
        if (cachedCustomers) this.customers = cachedCustomers;
        if (this.demoMode) {
            this.customers = [
                { codice: "CLI-DEMO-01", ragione_sociale: "Cliente dimostrativo" },
                { codice: "CLI-DEMO-02", ragione_sociale: "Secondo cliente dimostrativo" },
            ];
            this.deliveredArticles = [
                { codice: "ART-DEMO-01", descrizione: "Articolo consegnato dimostrativo", quantita_consegnata: "10" },
                { codice: "ART-DEMO-02", descrizione: "Secondo articolo consegnato", quantita_consegnata: "5" },
            ];
        } else {
            this.loadCustomers();
        }
        if (this.draft.customerCode && !this.demoMode) {
            await Promise.all([
                this.loadAddresses(this.draft.customerCode),
                this.loadDeliveredArticles(this.draft.customerCode),
            ]);
        }
    },
    methods: {
        customerLabel(customer) {
            return `${customer.codice} - ${customer.ragione_sociale}`;
        },
        addressLabel(address) {
            return `${address.codice} - ${address.descrizione}`;
        },
        setAddresses(customerCode, addresses) {
            if (this.draft.customerCode !== customerCode) return;
            this.addresses = addresses;
            if (addresses.length === 1) {
                this.draft.addressCode = addresses[0].codice;
                return;
            }
            if (!addresses.some((address) => address.codice === this.draft.addressCode)) {
                this.draft.addressCode = "";
            }
        },
        async loadCustomers() {
            this.loadingCustomers = true;
            try {
                const response = await this.$work.get("/arts/clienti/consegne-recenti/");
                this.customers = response.data.clienti;
                await this.$vlf.setItem(CUSTOMERS_CACHE_KEY, this.customers);
            } finally {
                this.loadingCustomers = false;
            }
        },
        async onCustomerChange(customerCode) {
            this.draft.addressCode = "";
            this.addresses = [];
            this.deliveredArticles = [];
            this.draft.lines.forEach((line) => {
                line.article = "";
                line.description = "";
            });
            this.createResult = null;
            if (!customerCode) return;
            if (this.demoMode) {
                this.addresses = [
                    { codice: "001", descrizione: "Sede principale" },
                    { codice: "002", descrizione: "Stabilimento secondario" },
                ];
                this.deliveredArticles = [
                    { codice: "ART-DEMO-01", descrizione: "Articolo consegnato dimostrativo", quantita_consegnata: "10" },
                    { codice: "ART-DEMO-02", descrizione: "Secondo articolo consegnato", quantita_consegnata: "5" },
                ];
                return;
            }
            await Promise.all([this.loadAddresses(customerCode), this.loadDeliveredArticles(customerCode)]);
        },
        async loadAddresses(customerCode) {
            if (this.demoMode) {
                this.addresses = [
                    { codice: "001", descrizione: "Sede principale" },
                    { codice: "002", descrizione: "Stabilimento secondario" },
                ];
                return;
            }
            const cacheKey = `${ADDRESSES_CACHE_PREFIX}_${customerCode}`;
            const cachedAddresses = await this.$vlf.getItem(cacheKey);
            if (cachedAddresses) this.setAddresses(customerCode, cachedAddresses);
            this.loadingAddresses = true;
            try {
                const response = await this.$work.get(`/arts/clienti/${encodeURIComponent(customerCode)}/indirizzi/`);
                this.setAddresses(customerCode, response.data);
                await this.$vlf.setItem(cacheKey, response.data);
            } finally {
                this.loadingAddresses = false;
            }
        },
        async loadDeliveredArticles(customerCode) {
            const cacheKey = `${DELIVERED_ARTICLES_CACHE_PREFIX}_${customerCode}_${TODAY_CACHE_SUFFIX}`;
            const cachedArticles = await this.$vlf.getItem(cacheKey);
            if (cachedArticles && this.draft.customerCode === customerCode) this.deliveredArticles = cachedArticles;
            this.loadingDeliveredArticles = true;
            try {
                const response = await this.$work.get(
                    `/arts/clienti/${encodeURIComponent(customerCode)}/articoli-consegnati/`
                );
                if (this.draft.customerCode === customerCode) this.deliveredArticles = response.data.articoli;
                await this.$vlf.setItem(cacheKey, response.data.articoli);
            } finally {
                this.loadingDeliveredArticles = false;
            }
        },
        addLine() {
            this.draft.lines.push(newReturnLine(`${Date.now()}-${this.draft.lines.length}`));
        },
        removeLine(index) {
            if (this.draft.lines.length > 1) this.draft.lines.splice(index, 1);
        },
        async saveDraft() {
            await this.$vlf.setItem(CUSTOMER_RETURN_DRAFT_KEY, this.draft);
            this.$toast.success("Bozza reso salvata soltanto su questo browser", { icon: "mdi-content-save" });
        },
        validateDraft() {
            this.errors = [...validateCustomerReturnDraft(this.draft), ...this.validateDeliveredQuantities()];
            if (this.errors.length) {
                this.$toast.error("Completare i campi obbligatori", { icon: "mdi-alert" });
            }
            return this.errors.length === 0;
        },
        validateDeliveredQuantities() {
            if (!this.draft.customerCode) return [];
            return validateDeliveredQuantities(this.draft.lines, this.deliveredArticles);
        },
        requestCreation() {
            this.createResult = null;
            if (!this.validateDraft()) return;
            if (this.demoMode) {
                this.$toast.error("La modalità dimostrativa non può scrivere in Sage", { icon: "mdi-alert" });
                return;
            }
            this.confirming = true;
        },
        async createReturn() {
            if (!this.validateDraft()) {
                this.confirming = false;
                return;
            }

            this.creating = true;
            try {
                const candidatePayload = buildCustomerReturnPayload(this.draft);
                const response = await this.$work.post("/arts/resi-cliente/", candidatePayload);
                this.createResult = response.data;
                this.confirming = false;
                await this.$vlf.removeItem(CUSTOMER_RETURN_DRAFT_KEY);
                this.$toast.success(response.data.message, { icon: "mdi-check" });
                this.$emit("created", response.data);
            } catch (error) {
                const responseErrors = error.response && error.response.data && error.response.data.errors;
                this.errors = responseErrors || ["Reso non creato: controllare i dati inseriti."];
                this.confirming = false;
                this.$toast.error("Il reso non è stato creato in Sage", { icon: "mdi-alert" });
            } finally {
                this.creating = false;
            }
        },
        async resetDraft() {
            this.draft = newCustomerReturnDraft();
            this.errors = [];
            this.createResult = null;
            this.confirming = false;
            this.addresses = [];
            this.deliveredArticles = [];
            await this.$vlf.removeItem(CUSTOMER_RETURN_DRAFT_KEY);
        },
    },
};
</script>
